import { zodResolver } from "@hookform/resolvers/zod";
import { CaretDown, Flask, MagicWand, Plus } from "@phosphor-icons/react";
import type { CoverLetterDto } from "@reactive-resume/dto";
import { createCoverLetterSchema } from "@reactive-resume/dto";
import { idSchema, sampleCoverLetter } from "@reactive-resume/schema";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Tooltip,
} from "@reactive-resume/ui";
import { cn, generateRandomName, slugify } from "@reactive-resume/utils";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";

import {
  useCreateCoverLetter,
  useDeleteCoverLetter,
  useUpdateCoverLetter,
} from "@/client/services/cover-letter";
import { useDialog } from "@/client/stores/dialog";

const formSchema = createCoverLetterSchema.extend({ id: idSchema.optional(), slug: z.string() });

type FormValues = z.infer<typeof formSchema>;

export const CoverLetterDialog = () => {
  const navigate = useNavigate();
  const { isOpen, mode, payload, close } = useDialog<CoverLetterDto>("cover-letter");

  const isCreate = mode === "create";
  const isUpdate = mode === "update";
  const isDelete = mode === "delete";
  const isDuplicate = mode === "duplicate";

  const { createCoverLetter, loading: createLoading } = useCreateCoverLetter();
  const { updateCoverLetter, loading: updateLoading } = useUpdateCoverLetter();
  const { deleteCoverLetter, loading: deleteLoading } = useDeleteCoverLetter();

  const loading = createLoading || updateLoading || deleteLoading;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { title: "", slug: "" },
  });

  useEffect(() => {
    if (isOpen) onReset();
  }, [isOpen, payload]);

  useEffect(() => {
    const slug = slugify(form.watch("title"));
    form.setValue("slug", slug);
  }, [form.watch("title")]);

  const onSubmit = async (values: FormValues) => {
    if (isCreate) {
      const created = await createCoverLetter({
        slug: values.slug,
        title: values.title,
        visibility: "private",
      });
      close();
      void navigate(`/cover-letter-builder/${created.id}`);
      return;
    }

    if (isUpdate) {
      if (!payload.item?.id) return;
      await updateCoverLetter({
        id: payload.item.id,
        title: values.title,
        slug: values.slug,
      });
    }

    if (isDuplicate) {
      if (!payload.item?.id) return;
      const created = await createCoverLetter({
        title: values.title,
        slug: values.slug,
        visibility: "private",
      });
      close();
      void navigate(`/cover-letter-builder/${created.id}`);
      return;
    }

    if (isDelete) {
      if (!payload.item?.id) return;
      await deleteCoverLetter({ id: payload.item.id });
    }

    close();
  };

  const onReset = () => {
    if (isCreate) form.reset({ title: "", slug: "" });
    if (isUpdate)
      form.reset({ id: payload.item?.id, title: payload.item?.title, slug: payload.item?.slug });
    if (isDuplicate)
      form.reset({ title: `${payload.item?.title} (Copy)`, slug: `${payload.item?.slug}-copy` });
    if (isDelete)
      form.reset({ id: payload.item?.id, title: payload.item?.title, slug: payload.item?.slug });
  };

  const handleGenerateRandomName = () => {
    const name = generateRandomName();
    form.setValue("title", name);
    form.setValue("slug", slugify(name));
  };

  const handleCreateSample = async () => {
    const randomName = generateRandomName();

    const created = await createCoverLetter({
      title: `${randomName} (Sample)`,
      slug: slugify(randomName),
      visibility: "private",
    });

    // Update the created cover letter with sample data
    await updateCoverLetter({
      id: created.id,
      data: sampleCoverLetter,
    });

    close();
    void navigate(`/cover-letter-builder/${created.id}`);
  };

  if (isDelete) {
    return (
      <AlertDialog open={isOpen} onOpenChange={close}>
        <AlertDialogContent>
          <Form {...form}>
            <form>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Are you sure you want to delete this cover letter?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete your cover letter and
                  cannot be recovered.
                </AlertDialogDescription>
              </AlertDialogHeader>

              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction variant="error" onClick={form.handleSubmit(onSubmit)}>
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </form>
          </Form>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent>
        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>
                <div className="flex items-center space-x-2.5">
                  <Plus />
                  <h2>
                    {isCreate && "Create a new cover letter"}
                    {isUpdate && "Update an existing cover letter"}
                    {isDuplicate && "Duplicate an existing cover letter"}
                  </h2>
                </div>
              </DialogTitle>
              <DialogDescription>
                {isCreate && "Start building your cover letter by giving it a name."}
                {isUpdate && "Changed your mind about the name? Give it a new one."}
                {isDuplicate && "Give your cover letter a new name."}
              </DialogDescription>
            </DialogHeader>

            <FormField
              name="title"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <div className="flex items-center justify-between gap-x-2">
                      <Input {...field} className="flex-1" />

                      {(isCreate || isDuplicate) && (
                        <Tooltip content="Generate a random title">
                          <Button
                            size="icon"
                            type="button"
                            variant="outline"
                            onClick={handleGenerateRandomName}
                          >
                            <MagicWand />
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  </FormControl>
                  <FormDescription>
                    Tip: Name it after the company or position you're applying to.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="slug"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Slug</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <div className="flex items-center">
                <Button
                  type="submit"
                  disabled={loading}
                  className={cn(isCreate && "rounded-r-none")}
                >
                  {isCreate && "Create"}
                  {isUpdate && "Save Changes"}
                  {isDuplicate && "Duplicate"}
                </Button>

                {isCreate && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" size="icon" className="rounded-l-none border-l">
                        <CaretDown />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side="right" align="center">
                      <DropdownMenuItem onClick={handleCreateSample}>
                        <Flask className="mr-2" />
                        Create Sample Cover Letter
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
